import React from 'react';
import Link from 'next/link';
import { useTranslation } from '../../hooks/useTranslation';
import Button from '../ui/Button';
import CommerceButton from '../ui/CommerceButton';
import DetailIcon from '../ui/DetailIcon';
import DropFacts, { formatDateTime } from './DropFacts';
import DropRegistrationForm from './DropRegistrationForm';
import useDropRegistration from './useDropRegistration';

export { formatDateTime } from './DropFacts';

const NOTICE_TONES = {
  gray: 'bg-gray-100 text-gray-700',
  green: 'bg-green-50 text-green-800',
  indigo: 'bg-indigo-50 text-indigo-900',
};

const Notice = ({ tone = 'gray', title, children }) => (
  <div role="status" className={`flex items-start gap-3 rounded-lg p-4 ${NOTICE_TONES[tone]}`}>
    <span className="mt-0.5 shrink-0"><DetailIcon name={tone === 'green' ? 'check' : tone === 'indigo' ? 'link' : 'clock'} /></span>
    <div>{title && <p className="mb-1 text-base font-bold">{title}</p>}<div className="text-sm leading-relaxed">{children}</div></div>
  </div>
);

function RegistrationControl({ state, code }) {
  const { t, locale } = useTranslation('drop');
  const { drop, viewer, authenticated, open, deadlinePassed, loginHref, checking, error, retry } = state;
  if (error) return <div role="alert" className="rounded-lg bg-krit-paper p-4">
    <p className="mb-3 text-sm">{t('loadError')}</p>
    <Button variant="secondary" onClick={retry}>{t('retry')}</Button>
  </div>;
  if (!drop && checking) return <p role="status" className="py-4 text-sm text-krit-muted">{t('checking')}</p>;
  if (!open) return <Notice>{deadlinePassed ? t('closedAt').replace('{datetime}', formatDateTime(drop.registrationEndsAt, locale)) : t('closed')}</Notice>;
  if (viewer.registered) return <Notice tone="green" title={t('registeredTitle')}>{t('registeredBody')}</Notice>;
  if (!authenticated) return <div>
    <CommerceButton availability="drop" href={loginHref} text={t('loginCta')} big />
    <p className="mt-3 text-xs leading-relaxed text-krit-muted">{t('loginHint')}</p>
  </div>;
  if (drop.access === 'discord' && !viewer.discordLinked) return <Notice tone="indigo" title={t('discordTitle')}>
    <p>{t('discordBody')}</p>
    <Link href="/cont/contul-meu" className="mt-2 inline-block font-semibold underline">{t('discordLink')}</Link>
  </Notice>;
  return <DropRegistrationForm code={code} {...state} />;
}

// The same control is embedded on products and used on standalone drop pages.
export default function DropRegistration({ drop: initial, code = initial?.code, returnTo, showDetails = false }) {
  const { t } = useTranslation('drop');
  const state = useDropRegistration({ initial, code, returnTo });
  return <div aria-busy={state.checking || state.submitting} className={showDetails ? 'flex flex-col gap-5' : undefined}>
    <div className="min-w-0">
      <RegistrationControl state={state} code={code} />
      {showDetails && <p className="mt-3 text-xs leading-relaxed text-krit-muted">{t('how.disclaimer')}</p>}
    </div>
    {showDetails && <div className="min-w-0">
      <h2 className="mb-4 flex items-center gap-2 text-base font-bold"><span aria-hidden="true" className="h-2 w-2 rounded-full bg-krit-drop" />{t('registrationTitle')}</h2>
      {state.drop && <DropFacts drop={state.drop} />}
    </div>}
  </div>;
}
